import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Search, X } from 'lucide-react';
import { capitalizeFirstLetters } from '../util/AddressUtil';
import { getApiSearchListingAutocomplete } from '../api/search-client';

interface Suggestion {
    id: string;
    localityName: string;
    state: string;
    postcode: string;
}

interface SearchBarProps {
    selectedItems?: Suggestion[];
    onItemsChange?: (items: Suggestion[]) => void;
    onSearch?: (items: Suggestion[]) => void;
    onFocus?: () => void;
    isCompact?: boolean;
    className?: string;
}

export const SharedSearchBar = ({
    selectedItems = [],
    onItemsChange,
    onSearch,
    onFocus,
    isCompact = false,
    className = '',
}: SearchBarProps) => {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const hiddenTextRef = useRef<HTMLSpanElement>(null);
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const lineHeightRef = useRef<HTMLDivElement>(null);

    const calculateTotalLineHeight = useCallback((): number => {
        if (lineHeightRef.current) {
            const styles = window.getComputedStyle(lineHeightRef.current);
            const lineHeight = parseFloat(styles.lineHeight);
            const verticalPadding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
            const marginBottom = parseFloat(styles.marginBottom);
            return lineHeight + verticalPadding + marginBottom;
        }
        return 40;
    }, []);

    const updateScrollableHeight = useCallback(() => {
        if (scrollContainerRef.current && containerRef.current) {
            // Only enable scrolling if there are selected items or active input
            const hasContent = selectedItems.length > 0 || inputValue.length > 0;
            
            if (hasContent) {
                const containerHeight = containerRef.current.getBoundingClientRect().height;
                const totalLineHeight = calculateTotalLineHeight();
                const maxHeight = 4 * totalLineHeight;
                
                if (containerHeight > maxHeight) {
                    scrollContainerRef.current.style.maxHeight = `${maxHeight}px`;
                    scrollContainerRef.current.style.overflowY = 'auto';
                } else {
                    scrollContainerRef.current.style.maxHeight = 'none';
                    scrollContainerRef.current.style.overflowY = 'visible';
                }
                // Always allow horizontal scroll when there's content
                scrollContainerRef.current.style.overflowX = 'auto';
            } else {
                // If there's no content (only placeholder), disable all scrolling
                scrollContainerRef.current.style.maxHeight = 'none';
                scrollContainerRef.current.style.overflowY = 'visible';
                scrollContainerRef.current.style.overflowX = 'hidden';
            }
        }
    }, [calculateTotalLineHeight, selectedItems.length, inputValue]);

    useEffect(() => {
        updateScrollableHeight();
    }, [selectedItems, updateScrollableHeight, inputValue]);

    const fetchSuggestions = async (query: string) => {
        if (!query.trim()) {
            setSuggestions([]);
            return;
        }
        try {
            const response = await getApiSearchListingAutocomplete({ query });
            setSuggestions(response || []);
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setSuggestions([]);
        }
    };

    const handleRemoveItem = (itemToRemove: Suggestion) => {
        const newItems = selectedItems.filter(item => item.id !== itemToRemove.id);
        onItemsChange?.(newItems);
        inputRef.current?.focus();
    };

    const handleSuggestionClick = (suggestion: Suggestion) => {
        const isAlreadySelected = selectedItems.some(item => item.id === suggestion.id);

        if (!isAlreadySelected) {
            const newItems = [...selectedItems, suggestion];
            onItemsChange?.(newItems);
        }

        setInputValue('');
        setSuggestions([]);
        inputRef.current?.focus();
    };

    const handleSearch = () => {
        if (onSearch) {
            onSearch(selectedItems);
        } else {
            const localityIds = selectedItems.map(item => item.id);
            const searchParams = new URLSearchParams();
            searchParams.append('localities', localityIds.join(','));
            navigate(`/search-results?${searchParams.toString()}`);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        fetchSuggestions(value);
    };

    const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const isClickable = (e.target as HTMLElement).closest('[data-selected-item]') || 
                          (e.target as HTMLElement).tagName === 'INPUT';

        if (!isClickable) {
            e.preventDefault();
            inputRef.current?.focus();
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsFocused(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const updateInputWidth = useCallback(() => {
        if (hiddenTextRef.current && inputRef.current) {
            const textToMeasure = inputValue || (selectedItems.length === 0 ? "Search by suburb... " : "");
            hiddenTextRef.current.textContent = textToMeasure;
            const textWidth = hiddenTextRef.current.getBoundingClientRect().width;
            
            // Only add minimal padding to the text width
            inputRef.current.style.width = `${textWidth + 2}px`;
        }
    }, [inputValue, selectedItems.length]);

    useEffect(() => {
        updateInputWidth();
        console.log("selected items", selectedItems);
    }, [inputValue, selectedItems, updateInputWidth]);

    const containerClasses = `relative flex-grow overflow-hidden border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-[#24ccf2] bg-white/90 backdrop-blur-sm shadow-lg hover:shadow-xl transition-shadow ${className}`;

    return (
        <div ref={containerRef} className="relative w-full">
            <div className={containerClasses}>
                <div className="flex items-stretch min-h-[56px]">
                    <div className="flex-grow flex items-stretch overflow-hidden">
                        {selectedItems.length === 0 && (
                            <div className="flex-none flex items-center pl-4 text-gray-400">
                                <MapPin className="h-5 w-5" />
                            </div>
                        )}
                        <div className="flex-grow relative min-w-0">
                            <div 
                                ref={scrollContainerRef}
                                className={`${selectedItems.length === 0 && !inputValue ? 'overflow-hidden' : 'overflow-x-auto'}`}
                                onClick={handleContainerClick}
                            >
                                <div 
                                    ref={containerRef}
                                    className="flex flex-wrap items-center gap-2 py-2 px-4 min-w-min"
                                >
                                    {selectedItems.map((item, index) => (
                                        <div
                                            key={item.id}
                                            data-selected-item
                                            ref={index === 0 ? lineHeightRef : null}
                                            className="bg-[#24ccf2]/10 text-[#2476f2] rounded-full px-3 py-1 flex items-center flex-shrink-0"
                                        >
                                            <span className="whitespace-nowrap">
                                                {capitalizeFirstLetters(item.localityName)}
                                            </span>
                                            <button
                                                onClick={() => handleRemoveItem(item)}
                                                className="text-[#2476f2]/70 hover:text-[#2476f2] ml-2"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={inputValue}
                                        onChange={handleInputChange}
                                        onFocus={() => {
                                            setIsFocused(true);
                                            onFocus?.();
                                        }}
                                        placeholder={selectedItems.length === 0 ? "Search by suburb..." : ""}
                                        className="outline-none text-lg bg-transparent h-full py-2 w-auto"
                                        style={{ 
                                            minWidth: selectedItems.length === 0 ? '220px' : '10px',
                                            width: selectedItems.length === 0 && !inputValue ? '100%' : 'auto',
                                            textOverflow: selectedItems.length === 0 && !inputValue ? 'ellipsis' : 'clip'
                                        }}
                                        autoComplete="off"
                                        autoCapitalize="off"
                                        autoCorrect="off"
                                        spellCheck="false"
                                    />
                                </div>
                            </div>
                            <span
                                ref={hiddenTextRef}
                                className="absolute opacity-0 overflow-hidden whitespace-pre text-lg"
                                style={{
                                    visibility: 'hidden',
                                    position: 'absolute',
                                    left: '-9999px',
                                    font: window.getComputedStyle(inputRef.current || document.body).font
                                }}
                            />
                        </div>
                    </div>
                    <div className="flex-none">
                        <button
                            onClick={handleSearch}
                            className="h-full w-14 flex items-center justify-center text-white bg-gradient-to-r from-[#24ccf2] to-[#2476f2] hover:opacity-90 transition-opacity"
                        >
                            <Search className="h-6 w-6" />
                        </button>
                    </div>
                </div>
            </div>

            {!isCompact && isFocused && suggestions && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-[300px] overflow-y-auto z-50">
                    {suggestions.map((suggestion) => {
                        const isSelected = selectedItems.some(item => item.id === suggestion.id);
                        return (
                            <button
                                key={suggestion.id}
                                className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between ${isSelected ? 'bg-[#24ccf2]/10' : ''}`}
                                onClick={() => handleSuggestionClick(suggestion)}
                            >
                                <span className="text-gray-700">
                                    {capitalizeFirstLetters(suggestion.localityName)}, {suggestion.state} {suggestion.postcode}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default SharedSearchBar;